import { SparklingBirdIcon } from "@/components/common/icons/SparklingBirdIcon/SparklingBirdIcon"
import { Progress } from "@/components/common/Progress/Progress"
import { Typography } from "@/components/common/Typography/Typography"
import { UpgradeLink } from "@/components/common/UpgradeLink/UpgradeLink"
import { FeedbackSubmissionModal } from "@/components/feature/Modals/FeedbackSubmissionModal"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { classNames } from "@/utils/classNames"
import React, { useState } from "react"

interface ProgressBarProps {
  progressValue: number
  className?: string
  usageLabel?: string
  /** Hide the bird, e.g. for compact per-resource rows. */
  hideIcon?: boolean
  /** Paint the bar red: nothing left of this resource. */
  exhausted?: boolean
  /** Accessible name of the bar. */
  label?: string
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  progressValue,
  className,
  usageLabel,
  hideIcon,
  exhausted,
  label,
}) => {
  return (
    <div className={classNames("flex flex-row items-center gap-2", className)}>
      {!hideIcon && <SparklingBirdIcon />}
      <Progress
        value={progressValue}
        aria-label={label}
        indicatorClassName={exhausted ? "bg-red1" : undefined}
      />
      {usageLabel && (
        <Typography variant="textXS" className="whitespace-nowrap text-grey5">
          {usageLabel}
        </Typography>
      )}
    </div>
  )
}

interface CardProps {
  className?: string
  children?: React.ReactNode
  dataTestId?: string
}

export const Card: React.FC<CardProps> = ({
  className,
  children,
  dataTestId,
}) => {
  return (
    <div
      className={classNames(
        "bg-gray-100 h-fit max-w-56 min-w-56 rounded-md bg-grey3 p-4",
        className,
      )}
      data-testid={dataTestId}
    >
      {children}
    </div>
  )
}

interface TextProps {
  className?: string
  text: {
    firstSection: string
    limitText: string
    lastSection: string
  }
}

export const Text: React.FC<TextProps> = ({ className, text }) => {
  return (
    <div className={className}>
      <Typography className="mt-2 text-grey5" variant="textXS">
        {text.firstSection}
        <Typography variant="textXS-bold" as="span">
          {text.limitText}{" "}
        </Typography>
        {text.lastSection}
      </Typography>
    </div>
  )
}

interface ButtonProps {
  className?: string
}

export const Buttons: React.FC<ButtonProps> = ({ className }) => {
  const { translate } = useTranslation()
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false)

  return (
    <div className={className}>
      <Typography
        className="mt-2 flex justify-between gap-2 truncate text-white"
        variant={"textXS"}
      >
        <button
          onClick={() => setIsFeedbackModalOpen(true)}
          className="hover:underline"
        >
          {translate("FREE_TRIAL/FEEDBACK")}
        </button>
        {UpgradeButton({})}
      </Typography>
      <FeedbackSubmissionModal
        open={isFeedbackModalOpen}
        onOpenChange={setIsFeedbackModalOpen}
      />
    </div>
  )
}

const FreeTrial = {
  Text,
  Card,
  ProgressBar,
  Buttons,
}

export default FreeTrial

export function UpgradeButton(props: { className?: string }) {
  const { translate } = useTranslation()
  return (
    <UpgradeLink>
      <button
        className={classNames(
          "truncate text-yellow1 hover:underline",
          props.className,
        )}
      >
        {translate("FREE_TRIAL/UPGRADE")}
      </button>
    </UpgradeLink>
  )
}
